import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Header() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const nav = useNavigate();

    const linkCls = ({ isActive }) =>
        `text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"}`;

    const handleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <header
            className="sticky top-0 z-40 w-full border-b border-border/70 glass"
            data-testid="site-header"
        >
            <div className="container-app flex h-16 md:h-20 items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
                    <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg">V</div>
                    <span className="font-display text-xl md:text-2xl font-semibold tracking-tight">Venuely</span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    <NavLink to="/search" className={linkCls} data-testid="nav-search">Find venues</NavLink>
                    <NavLink to="/how-it-works" className={linkCls} data-testid="nav-how">How it works</NavLink>
                    <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</NavLink>
                    <NavLink to="/list-your-venue" className={linkCls} data-testid="nav-list">List your venue</NavLink>
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                                <button className="flex items-center gap-2 rounded-full border border-border px-1 py-1 hover:bg-muted/70">
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={user.picture} />
                                        <AvatarFallback>{(user.name || user.email || "U")[0].toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <span className="pr-2 text-sm font-medium max-w-[140px] truncate">{user.name || user.email}</span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs text-muted-foreground">{user.email}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                                    <LayoutDashboard className="mr-2 h-4 w-4" /> Owner dashboard
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                                        <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                                    <LogOut className="mr-2 h-4 w-4" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <button onClick={handleLogin} className="text-sm font-medium text-foreground/80 hover:text-foreground" data-testid="btn-login">
                                Log in
                            </button>
                            <Button asChild className="rounded-full px-5 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="btn-list-your-venue-hero">
                                <Link to="/list-your-venue">List your venue</Link>
                            </Button>
                        </>
                    )}
                </div>

                <button
                    className="md:hidden p-2 -mr-2 rounded-full hover:bg-muted/70"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-border bg-background">
                    <div className="container-app py-4 flex flex-col gap-3">
                        <Link to="/search" onClick={() => setOpen(false)} className="py-2 text-base" data-testid="m-nav-search">Find venues</Link>
                        <Link to="/how-it-works" onClick={() => setOpen(false)} className="py-2 text-base">How it works</Link>
                        <Link to="/pricing" onClick={() => setOpen(false)} className="py-2 text-base">Pricing</Link>
                        <Link to="/list-your-venue" onClick={() => setOpen(false)} className="py-2 text-base">List your venue</Link>
                        {user ? (
                            <>
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-base">Dashboard</Link>
                                {user.role === "admin" && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-base">Admin</Link>
                                )}
                                <Button variant="outline" onClick={async () => { await logout(); nav("/"); setOpen(false); }}>Log out</Button>
                            </>
                        ) : (
                            <Button onClick={handleLogin} className="w-full rounded-full bg-primary text-primary-foreground">Log in / Sign up</Button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
